import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from "react";
import {
  Card,
  Table,
  Form,
  Button,
  Row,
  Col,
  Pagination,
  Nav,
} from "react-bootstrap";
import { MdDelete } from "react-icons/md";
import { FaEdit, FaEye, FaPlus, FaTrash } from "react-icons/fa";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { GlobalModal } from "../../helper/GlobalModal";
import ConfirmationModal from "../../helper/ConfirmationModal";
import TooltipWrapper from "../../helper/Tooltip";
import { showNotification } from "../../service/showNotification";
import {
  getAssetColumns,
  getTsaBillingTypes,
  getTsaBillingTypeById,
  deleteTsaBillingType,
  updateTsaBillingType,
  getContactGroup,
} from "../../service/api";
import styles from "./index.module.css";
import clsx from "clsx";
import { TSAConditionRow } from "../TsaModal/TsaConditionRow";
import { tracker } from "../../service/mixpanel";

const TsaBackendTable = forwardRef(
  (
    {
      isNewUploaded,
      searchTerm,
      setTotalTsaTypes,
      setIsLoading,
      isLoading,
      onRefresh,
    },
    ref
  ) => {
    const [data, setData] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [editRow, setEditRow] = useState({});
    const [isChanged, setIsChanged] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [viewLoading, setViewLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [isDeleteConfirmationModal, setIsDeleteConfirmationModal] =
      useState(false);
    const [selectedTsaForDelete, setSelectedTsaForDelete] = useState(null);
    const [assetColumns, setAssetColumns] = useState([]);
    const [editConditions, setEditConditions] = useState([
      { columnId: "", operator: "", values: [], valueRange: [], dataType: "" },
    ]);
    const [conditionErrors, setConditionErrors] = useState({});
    const [editFormLoading, setEditFormLoading] = useState(false);
    const [yearlyRatesError, setYearlyRatesError] = useState("");
    const [contactGroupOptions, setContactGroupOptions] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const paginationOptions = [
      { value: 5, label: "5" },
      { value: 10, label: "10" },
      { value: 25, label: "25" },
      { value: 50, label: "50" },
      { value: 100, label: "100" },
    ];

    const {
      control,
      handleSubmit,
      formState: { errors },
      reset,
      setValue,
    } = useForm({
      defaultValues: {
        yearlyRates: Array.from({ length: 21 }, (_, index) => ({
          year: index + 1,
          tsaRate: "",
          pcRate: "",
        })),
      },
    });

    const debounce = (func, delay) => {
      let timeoutId;
      return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(null, args), delay);
      };
    };

    useImperativeHandle(ref, () => ({
      refreshData: () =>
        fetchTsaBillingTypes(currentPage, searchTerm, itemsPerPage),
    }));

    const fetchTsaBillingTypes = useCallback(
      async (page = 1, search = "", limit = itemsPerPage) => {
        try {
          if (search && search.length > 0) {
            tracker.tsaManagement.tsaManagementSearch(search);
          }
          setIsLoading(true);
          const response = await getTsaBillingTypes({
            page,
            limit,
            search,
            sortBy: "tsaType",
            sortOrder: "asc",
          });
          const tsaData = response?.data?.tsaBillingTypes || [];
          const pagination = response?.data?.pagination || {};
          setData(tsaData);
          setCurrentPage(pagination.currentPage || page);
          setTotalPages(pagination.totalPages || 1);
          setTotalRecords(pagination.totalRecords || tsaData.length);
          setTotalTsaTypes(pagination.totalRecords || tsaData.length);
        } catch (err) {
          console.log("err", err);
          setData([]);
          setCurrentPage(1);
          setTotalPages(1);
          setTotalRecords(0);
          setTotalTsaTypes(0);
          showNotification("error", "Failed to fetch TSA billing types.");
        } finally {
          setIsLoading(false);
        }
      },
      [itemsPerPage, setIsLoading, setTotalTsaTypes]
    );

    const debouncedSearch = useCallback(
      debounce((searchValue) => {
        setCurrentPage(1);
        fetchTsaBillingTypes(1, searchValue, itemsPerPage);
      }, 300),
      [fetchTsaBillingTypes, itemsPerPage]
    );

    const handlePageChange = (newPage) => {
      if (newPage >= 1 && newPage <= totalPages) {
        setCurrentPage(newPage);
        fetchTsaBillingTypes(newPage, searchTerm, itemsPerPage);
      }
    };

    const handleItemsPerPageChange = (selectedOption) => {
      if (selectedOption) {
        setItemsPerPage(selectedOption.value);
        setCurrentPage(1);
        fetchTsaBillingTypes(1, searchTerm, selectedOption.value);
      }
    };

    const showPageNumbers = (pageIndex, currentPage, totalPages) => {
      const page = pageIndex + 1;
      if (totalPages <= 7) return true;
      if (page <= 3) return true;
      if (page >= totalPages - 2) return true;
      if (Math.abs(page - currentPage) <= 1) return true;
      return false;
    };

    const fetchColumns = async () => {
      try {
        setEditFormLoading(true);
        const res = await getAssetColumns();
        //  Filter out unwanted columns
        const filteredColumns = (res?.data || []).filter(
          (col) => !col.isDocument && col.label?.toLowerCase() !== "action"
        );
        setAssetColumns(filteredColumns ?? []);
      } catch (err) {
        console.log("err", err);
        setAssetColumns([]);
        showNotification("error", "Failed to fetch columns.");
      } finally {
        setEditFormLoading(false);
      }
    };

    const getContactGroupOptions = async () => {
      try {
        const response = await getContactGroup();
        const data = response.data.data;
        if (data) {
          const transformedData = data.map((item) => ({
            label: item.contactGroupName,
            value: item.contactGroupName,
          }));
          setContactGroupOptions(transformedData);
        }
      } catch (error) {
        console.log("Something went wrong");
      }
    };

    // Validate a single condition
    const validateCondition = (condition, index) => {
      const errors = {};
      if (!condition.columnId) {
        errors.columnId = "Column is required";
      }
      if (!condition.operator) {
        errors.operator = "Operator is required";
      }
      if (
        (!condition.values?.length && !condition.valueRange?.length) ||
        (condition.operator === "isBetween" &&
          (!condition.valueRange[0] || !condition.valueRange[1]))
      ) {
        errors.values = "Value(s) are required";
      }
      return errors;
    };

    const addEditCondition = () => {
      const newCondition = {
        columnId: "",
        operator: "",
        values: [],
        valueRange: [],
        dataType: "",
      };
      setEditConditions([...editConditions, newCondition]);

      // Validate the new condition
      setIsChanged(true);
    };

    const removeEditCondition = (index) => {
      if (editConditions.length > 1) {
        tracker.tsaManagement.deleteTsaMamaClick()
        const newConditions = editConditions.filter((_, i) => i !== index);
        setEditConditions(newConditions);

        const newErrors = { ...conditionErrors };
        delete newErrors[`${index}`];
        const reindexedErrors = {};
        Object.keys(newErrors).forEach((key) => {
          const idx = parseInt(key);
          if (idx > index) {
            reindexedErrors[`${idx - 1}`] = newErrors[key];
          } else {
            reindexedErrors[key] = newErrors[key];
          }
        });
        setConditionErrors(reindexedErrors);
        setIsChanged(true);
      }
    };

    const handleEditConditionChange = (index, updated) => {
      const newConditions = [...editConditions];
      newConditions[index] = updated;
      setEditConditions(newConditions);
      setIsChanged(true);
    };

    const handleEdit = async (row, index) => {
      tracker.tsaManagement.editTsaType();
      try {
        setEditFormLoading(true);
        setEditIndex(index);
        setEditRow({ ...row });
        const response = await getTsaBillingTypeById(row._id);
        const tsaDetails = response?.data || {};

        reset({
          yearlyRates: Array.from({ length: 21 }, (_, yearIndex) => {
            const existingRate = tsaDetails.yearlyRates?.find(
              (rate) => rate.year === yearIndex + 1
            );
            return {
              year: yearIndex + 1,
              tsaRate: existingRate?.tsaRate || "",
              pcRate: existingRate?.pcRate || "",
            };
          }),
        });

        if (String(row.tsaType).trim() !== "1") {
          const newConditions =
            tsaDetails.conditions && tsaDetails.conditions.length > 0
              ? tsaDetails.conditions.map((condition) => ({
                  columnId: condition.columnId || "",
                  operator: condition.operator || "",
                  values: condition.values || condition.valueRange || [],
                  valueRange: condition.valueRange || [],
                  dataType:
                    assetColumns?.find((col) => col._id === condition.columnId)
                      ?.dataType || "",
                }))
              : [
                  {
                    columnId: "",
                    operator: "",
                    values: [],
                    valueRange: [],
                    dataType: "",
                  },
                ];
          setEditConditions(newConditions);

          // Validate all conditions
          const newErrors = {};
          newConditions.forEach((condition, idx) => {
            const errors = validateCondition(condition, idx);
            if (Object.keys(errors).length > 0) {
              newErrors[`${idx}`] = errors;
            }
          });
          setConditionErrors(newErrors);
        } else {
          setEditConditions([]);
          setConditionErrors({});
        }
        setIsChanged(false);
        setYearlyRatesError("");
        setEditModalOpen(true);
      } catch (error) {
        console.error("Error fetching TSA details for edit:", error);
        showNotification("error", "Failed to fetch TSA details for editing");
      } finally {
        setEditFormLoading(false);
      }
    };

    const handleView = async (row) => {
      tracker.tsaManagement.viewTsaType(row._id);
      try {
        setViewLoading(true);
        setViewModalOpen(true);
        const response = await getTsaBillingTypeById(row._id);
        const tsaDetails = response?.data || {};
        setViewData(tsaDetails);
      } catch (error) {
        console.error("Error fetching TSA details:", error);
        showNotification("error", "Failed to fetch TSA details");
        setViewModalOpen(false);
      } finally {
        setViewLoading(false);
      }
    };

    const toggleDeleteConfirmationModal = (tsaData = null) => {
      setIsDeleteConfirmationModal((prev) => !prev);
      setSelectedTsaForDelete(tsaData);
    };

    const handleDelete = (row, index) => {
      tracker.tsaManagement.deleteTsaMamaClick();
      toggleDeleteConfirmationModal(row);
    };

    const handleConfirmDelete = async () => {
      if (!selectedTsaForDelete) return;
      try {
        setDeleteLoading(selectedTsaForDelete._id);
        const response = await deleteTsaBillingType(selectedTsaForDelete._id);
        toggleDeleteConfirmationModal();
        showNotification(
          "success",
          response?.message || "TSA Type deleted successfully"
        );
        await fetchTsaBillingTypes(currentPage, searchTerm, itemsPerPage);
      } catch (error) {
        console.error("Error deleting TSA:", error);
        toggleDeleteConfirmationModal();
        showNotification(
          "error",
          error?.response?.data?.message ||
            error.message ||
            "Failed to delete TSA Type"
        );
      } finally {
        setDeleteLoading(null);
      }
    };

    const handleChange = (field, value) => {
      setIsChanged(true);
    };

    const validateYearlyRates = (formData) => {
      if (editRow.contactGroupType === "AmpVantage Energy") {
        const firstSixYearsValid = formData.yearlyRates
          .slice(0, 6)
          .every((rate) => rate.tsaRate && parseFloat(rate.tsaRate) >= 0);
        if (!firstSixYearsValid) {
          setYearlyRatesError(
            "AmpVantage should have first 6 year record as using in Tranche"
          );
          return false;
        }
      }
      const hasAtLeastOneCompleteYear = formData.yearlyRates.some(
        (rate) =>
          rate.tsaRate &&
          parseFloat(rate.tsaRate) >= 0 &&
          rate.pcRate &&
          parseFloat(rate.pcRate) >= 0
      );
      if (!hasAtLeastOneCompleteYear) {
        setYearlyRatesError(
          "At least one year must have both TSA Rate and P&C Rate filled"
        );
        return false;
      }
      const hasNegativeValues = formData.yearlyRates.some(
        (rate) =>
          (rate.tsaRate && parseFloat(rate.tsaRate) < 0) ||
          (rate.pcRate && parseFloat(rate.pcRate) < 0)
      );
      if (hasNegativeValues) {
        setYearlyRatesError("TSA Rate and P&C Rate cannot be negative");
        return false;
      }
      setYearlyRatesError("");
      return true;
    };

    const onEditSubmit = async (formData) => {
      tracker.tsaManagement.updateTsaType();
      // Validate conditions
      if (String(editRow.tsaType).trim() !== "1") {
        const newErrors = {};
        editConditions.forEach((condition, index) => {
          if (condition.operator !== "isBetween") {
            editConditions[index].valueRange = [];
          }
        });
        editConditions.forEach((condition, index) => {
          const errors = validateCondition(condition, index);
          if (Object.keys(errors).length > 0) {
            newErrors[`${index}`] = errors;
          }
        });
        setConditionErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
          showNotification("error", "Please fill all condition fields");
          return false;
        }
      }

      // Validate yearly rates
      if (!validateYearlyRates(formData)) {
        return false;
      }

      try {
        setEditFormLoading(true);

        let processedConditions = [];
        if (String(editRow.tsaType).trim() !== "1") {
          processedConditions = editConditions
            .filter(
              (cond) =>
                cond.columnId &&
                cond.operator &&
                (cond.values?.length || cond.valueRange?.length)
            )
            .map((cond) => {
              const selectedColumn = assetColumns.find(
                (col) => col._id === cond.columnId
              );
              return {
                columnId: cond.columnId,
                columnName: selectedColumn?.label || cond.columnId,
                operator: cond.operator,
                ...(cond.operator === "isBetween"
                  ? { valueRange: cond.values }
                  : { values: cond.values }),
                dataType: selectedColumn?.dataType || "",
                valueRange: cond.valueRange,
                values: cond.values,
              };
            });
        }

        const processedYearlyRates = formData.yearlyRates
          .filter(
            (rate) =>
              (rate.tsaRate && parseFloat(rate.tsaRate) >= 0) ||
              (rate.pcRate && parseFloat(rate.pcRate) >= 0)
          )
          .map((rate) => ({
            year: rate.year,
            tsaRate: parseFloat(rate.tsaRate) || 0,
            pcRate: parseFloat(rate.pcRate) || 0,
          }));

        const tsaUpdateData = {
          conditions: processedConditions,
          yearlyRates: processedYearlyRates,
          contactGroupType: editRow.contactGroupType,
        };

        const response = await updateTsaBillingType(editRow._id, tsaUpdateData);
        showNotification(
          "success",
          response?.data?.message || "TSA Type updated successfully"
        );

        setEditModalOpen(false);
        setEditIndex(null);
        setEditRow({});
        setEditConditions([
          {
            columnId: "",
            operator: "",
            values: [],
            valueRange: [],
            dataType: "",
          },
        ]);
        setConditionErrors({});
        setIsChanged(false);
        await fetchTsaBillingTypes(currentPage, searchTerm, itemsPerPage);
      } catch (error) {
        console.error("Error updating TSA:", error);
        showNotification(
          "error",
          error?.response?.data?.message ||
            error.message ||
            "Failed to update TSA Type"
        );
      } finally {
        setEditFormLoading(false);
      }
    };

    const renderConditions = (conditions) => {
      if (!conditions || conditions.length === 0) {
        return <span className="text-muted">No conditions</span>;
      }
      return (
        <span>
          {conditions.map((condition, index) => (
            <span key={index}>
              <strong>{condition.columnName}:</strong>{" "}
              {condition.operator === "isBetween"
                ? condition.valueRange.join(" - ")
                : condition.values.join(", ")}
              {index < conditions.length - 1 && ", "}
            </span>
          ))}
        </span>
      );
    };

    const canDeleteTsaType = (tsaType) => {
      return String(tsaType).trim() !== "1";
    };

    useEffect(() => {
      fetchTsaBillingTypes(1, "", itemsPerPage);
    }, [isNewUploaded, fetchTsaBillingTypes, itemsPerPage]);

    useEffect(() => {
      if (searchTerm !== undefined) {
        debouncedSearch(searchTerm);
      }
    }, [searchTerm, debouncedSearch]);

    useEffect(() => {
      fetchColumns();
      getContactGroupOptions();
    }, []);

    return (
      <>
        <div className="mt-4">
          <Card
            border="light"
            className="table-wrapper table-responsive shadow-sm"
          >
            <Card.Body className="p-0">
              <Table
                hover
                className="user-table align-items-center table-striped"
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        padding: "16px",
                      }}
                    >
                      TSA TYPE
                    </th>
                    <th
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        padding: "16px",
                      }}
                    >
                      CONDITIONS
                    </th>
                    <th
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        padding: "16px",
                      }}
                    >
                      ACTION
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading ? (
                    <tr>
                      <td
                        colSpan="3"
                        className="text-center py-4"
                        style={{ fontSize: "16px", padding: "24px" }}
                      >
                        Loading TSA Types...
                      </td>
                    </tr>
                  ) : data.length === 0 ? (
                    <tr>
                      <td
                        colSpan="3"
                        className="text-center py-4"
                        style={{ fontSize: "16px", padding: "24px" }}
                      >
                        {searchTerm
                          ? `No TSA Types found for "${searchTerm}"`
                          : "No TSA Types found"}
                      </td>
                    </tr>
                  ) : (
                    data.map((row, index) => (
                      <tr key={row._id}>
                        <td style={{ padding: "16px" }}>
                          <span
                            style={{
                              fontSize: "18px",
                              fontWeight: "700",
                              color: "#333",
                            }}
                          >
                            {row.tsaType}
                          </span>
                        </td>
                        <td style={{ padding: "16px" }}>
                          {renderConditions(row.conditions)}
                        </td>
                        <td style={{ padding: "16px" }}>
                          <TooltipWrapper tooltip="View TSA" placement="top">
                            <FaEye
                              className={clsx(
                                styles.viewIcon,
                                styles.icon,
                                "me-3"
                              )}
                              onClick={() => handleView(row)}
                              style={{
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                fontSize: "18px",
                                color: "blue",
                              }}
                            />
                          </TooltipWrapper>
                          <TooltipWrapper tooltip="Edit TSA" placement="top">
                            <FaEdit
                              className={clsx(
                                styles.editIcon,
                                styles.icon,
                                "me-3"
                              )}
                              onClick={() => handleEdit(row, index)}
                              style={{
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                fontSize: "18px",
                                color: "green",
                              }}
                            />
                          </TooltipWrapper>
                          {canDeleteTsaType(row.tsaType) && (
                            <TooltipWrapper tooltip="Delete" placement="top">
                              <MdDelete
                                className={clsx(styles.deleteIcon, styles.icon)}
                                onClick={() => handleDelete(row, index)}
                                style={{
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  fontSize: "20px",
                                  color: "red",
                                  opacity: deleteLoading === row._id ? 0.5 : 1,
                                  pointerEvents:
                                    deleteLoading === row._id ? "none" : "auto",
                                }}
                              />
                            </TooltipWrapper>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
            <Card.Footer
              className={clsx(
                "px-3 border-0 d-lg-flex justify-content-between",
                styles.stickyFooter
              )}
            >
              <Nav>
                <Pagination className="mb-2 mb-lg-0">
                  <Pagination.Prev
                    onClick={() =>
                      handlePageChange(Math.max(currentPage - 1, 1))
                    }
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Pagination.Prev>
                  {totalPages > 10 && (
                    <>
                      {currentPage > 4 && (
                        <>
                          <Pagination.Item onClick={() => handlePageChange(1)}>
                            1
                          </Pagination.Item>
                          <Pagination.Ellipsis disabled />
                        </>
                      )}
                      {[...Array(totalPages)].map((_, page) => {
                        if (showPageNumbers(page, currentPage, totalPages)) {
                          return (
                            <Pagination.Item
                              key={page + 1}
                              active={page + 1 === currentPage}
                              onClick={() => handlePageChange(page + 1)}
                            >
                              {page + 1}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}
                      {currentPage < totalPages - 3 && (
                        <>
                          <Pagination.Ellipsis disabled />
                          <Pagination.Item
                            onClick={() => handlePageChange(totalPages)}
                          >
                            {totalPages}
                          </Pagination.Item>
                        </>
                      )}
                    </>
                  )}
                  <Pagination.Next
                    onClick={() =>
                      handlePageChange(Math.min(currentPage + 1, totalPages))
                    }
                    disabled={currentPage === totalPages || totalPages === 0}
                  >
                    Next
                  </Pagination.Next>
                </Pagination>
              </Nav>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                Items Per Page
                <Select
                  options={paginationOptions}
                  value={paginationOptions.find(
                    (opt) => opt.value === itemsPerPage
                  )}
                  onChange={handleItemsPerPageChange}
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      minHeight: "32px",
                      height: "32px",
                      fontSize: "14px",
                    }),
                    valueContainer: (provided) => ({
                      ...provided,
                      height: "32px",
                      padding: "0 6px",
                    }),
                    indicatorsContainer: (provided) => ({
                      ...provided,
                      height: "32px",
                    }),
                    option: (provided) => ({
                      ...provided,
                      fontSize: "14px",
                      padding: "6px 12px",
                      minHeight: "28px",
                    }),
                    menuList: (provided) => ({
                      ...provided,
                      paddingTop: 0,
                      paddingBottom: 0,
                    }),
                  }}
                  menuPlacement="top"
                />
              </div>
            </Card.Footer>
          </Card>
        </div>

        <GlobalModal
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          title="Edit TSA Type"
          size="xl"
        >
          <Form onSubmit={handleSubmit(onEditSubmit)}>
            <div style={{ maxHeight: "70vh", overflowY: "auto" }}>
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Basic Information</h6>
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-0">
                    Update the conditions and yearly rates for this TSA type.
                  </p>
                </Card.Body>
              </Card>

              {String(editRow.tsaType).trim() !== "1" && (
                <Card className="mb-4">
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">Conditions</h6>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={addEditCondition}
                    >
                      <FaPlus className="me-1" /> Add Condition
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    {editConditions.map((condition, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-start mb-3"
                      >
                        <div className="flex-grow-1">
                          <TSAConditionRow
                            columns={assetColumns}
                            condition={condition}
                            onChange={(updated) =>
                              handleEditConditionChange(index, updated)
                            }
                            errors={conditionErrors[`${index}`] || {}}
                          />
                        </div>
                        {editConditions.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className="ms-2"
                            onClick={() => removeEditCondition(index)}
                            style={{
                              height: "38px",
                              width: "38px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              marginTop: "2rem",
                            }}
                          >
                            <FaTrash />
                          </Button>
                        )}
                      </div>
                    ))}
                  </Card.Body>
                </Card>
              )}

              <Card className="mb-4">
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">Select Contact Group Type</h6>
                </Card.Header>
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={2}>
                      <Form.Label className="mb-0">
                        Contact Group Type
                      </Form.Label>
                    </Col>
                    <Col md={10}>
                      <Select
                        placeholder="Select type"
                        value={
                          contactGroupOptions?.find(
                            (opt) => opt.value === editRow.contactGroupType
                          ) || null
                        }
                        onChange={(selected) => {
                          setEditRow((prev) => ({
                            ...prev,
                            contactGroupType: selected?.value || "",
                          }));
                          setIsChanged(true);
                        }}
                        options={contactGroupOptions}
                        isLoading={editFormLoading}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: "38px",
                            height: "38px",
                            fontSize: "14px",
                            borderColor: "#ced4da",
                            borderRadius: "0.375rem",
                          }),
                          valueContainer: (base) => ({
                            ...base,
                            height: "36px",
                            padding: "0 12px",
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: "36px",
                          }),
                        }}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">
                    Yearly Rates (21 Years) - At least 1 year must have both
                    rates filled
                  </h6>
                </Card.Header>
                <Card.Body>
                  {yearlyRatesError && (
                    <div className="alert alert-danger mb-3" role="alert">
                      {yearlyRatesError}
                    </div>
                  )}
                  <div
                    style={{
                      maxHeight: "400px",
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      {Array.from({ length: 21 }, (_, index) => (
                        <Row
                          key={index}
                          className="mb-2 align-items-center"
                          style={{ margin: "0", width: "100%" }}
                        >
                          <Col
                            xs={12}
                            sm={4}
                            md={4}
                            style={{ paddingLeft: "0", paddingRight: "8px" }}
                          >
                            <Form.Label
                              className="mb-0"
                              style={{ fontSize: "14px", fontWeight: "500" }}
                            >
                              Year {index + 1}
                            </Form.Label>
                          </Col>
                          <Col
                            xs={6}
                            sm={4}
                            md={4}
                            style={{ paddingLeft: "8px", paddingRight: "8px" }}
                          >
                            <Controller
                              name={`yearlyRates.${index}.tsaRate`}
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="number"
                                  step="0.000000000000001"
                                  min="0"
                                  placeholder="TSA Rate $"
                                  size="sm"
                                  style={{ width: "100%" }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    handleChange(
                                      `yearlyRates.${index}.tsaRate`,
                                      e.target.value
                                    );
                                    setYearlyRatesError("");
                                  }}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "-" ||
                                      e.key === "e" ||
                                      e.key === "E"
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              )}
                            />
                          </Col>
                          <Col
                            xs={6}
                            sm={4}
                            md={4}
                            style={{ paddingLeft: "8px", paddingRight: "0" }}
                          >
                            <Controller
                              name={`yearlyRates.${index}.pcRate`}
                              control={control}
                              render={({ field }) => (
                                <Form.Control
                                  {...field}
                                  type="number"
                                  step="0.000000000000001"
                                  min="0"
                                  placeholder="P&C Rate $"
                                  size="sm"
                                  style={{ width: "100%" }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    handleChange(
                                      `yearlyRates.${index}.pcRate`,
                                      e.target.value
                                    );
                                    setYearlyRatesError("");
                                  }}
                                  onKeyDown={(e) => {
                                    if (
                                      e.key === "-" ||
                                      e.key === "e" ||
                                      e.key === "E"
                                    ) {
                                      e.preventDefault();
                                    }
                                  }}
                                />
                              )}
                            />
                          </Col>
                        </Row>
                      ))}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
              <Button
                variant="secondary"
                onClick={() => setEditModalOpen(false)}
                disabled={editFormLoading}
                style={{
                  marginRight: "10px",
                  color: "#6c757d",
                  borderColor: "#6c757d",
                  backgroundColor: "transparent",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={editFormLoading || !isChanged}
              >
                {editFormLoading ? "Updating..." : "Update TSA Type"}
              </Button>
            </div>
          </Form>
        </GlobalModal>

        <GlobalModal
          isOpen={viewModalOpen}
          onClose={() => {
            setViewModalOpen(false);
            setViewData(null);
          }}
          title="TSA Type Details"
          size="xl"
        >
          {viewLoading ? (
            <div className="text-center py-4">
              <div>Loading TSA details...</div>
            </div>
          ) : viewData ? (
            <div>
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">TSA Information</h6>
                </Card.Header>
                <Card.Body>
                  <Table bordered size="sm">
                    <tbody>
                      <tr>
                        <td style={{ width: "200px" }}>
                          <strong>TSA Type</strong>
                        </td>
                        <td>{viewData.tsaType}</td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Created At</strong>
                        </td>
                        <td>
                          {new Date(viewData.createdAt).toLocaleDateString()}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <strong>Updated At</strong>
                        </td>
                        <td>
                          {new Date(viewData.updatedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Conditions</h6>
                </Card.Header>
                <Card.Body>
                  <Table bordered size="sm">
                    <thead>
                      <tr>
                        <th>Column Name</th>
                        <th>Operator</th>
                        <th>Values</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewData.conditions && viewData.conditions.length > 0 ? (
                        viewData.conditions.map((condition, index) => (
                          <tr key={index}>
                            <td>
                              <strong>{condition.columnName}</strong>
                            </td>
                            <td>{condition.operator}</td>
                            <td>
                              {condition.operator === "isBetween"
                                ? condition.valueRange.join(" - ")
                                : condition.values.join(", ")}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="3" className="text-center text-muted">
                            No conditions defined
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
              <Card className="mb-4">
                <Card.Header>
                  <h6 className="mb-0">Yearly Rates</h6>
                </Card.Header>
                <Card.Body>
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table bordered size="sm">
                      <thead>
                        <tr>
                          <th>Year</th>
                          <th>TSA Rate ($)</th>
                          <th>P&C Rate ($)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {viewData.yearlyRates &&
                        viewData.yearlyRates.length > 0 ? (
                          viewData.yearlyRates
                            .filter((rate) => rate.year > 0)
                            .map((rate, index) => (
                              <tr key={index}>
                                <td>
                                  <strong>Year {rate.year}</strong>
                                </td>
                                <td>{rate.tsaRate}$</td>
                                <td>{rate.pcRate}$</td>
                              </tr>
                            ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="text-center text-muted">
                              No yearly rates defined
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </div>
          ) : (
            <div className="text-center py-4">
              <div>No data available</div>
            </div>
          )}
        </GlobalModal>

        <ConfirmationModal
          onConfirm={handleConfirmDelete}
          onHide={() => toggleDeleteConfirmationModal()}
          show={isDeleteConfirmationModal}
          title="Delete TSA Type"
          message={`Are you sure you want to delete TSA Type "${selectedTsaForDelete?.tsaType}"?`}
          confirmText="Delete"
          loading={deleteLoading === selectedTsaForDelete?._id}
        />
      </>
    );
  }
);

export default TsaBackendTable;
